import BookShow from "components/shared/book/show";
import Loader from "components/shared/loader";
import { BG_TASK_TYPES } from "helpers/bgTasks";
import { dateTimeToView, dateToView, utcTimeInOffset } from "helpers/date";
import { organizationFlags, organizationFlagsEnum, workloadStatusesEnum } from "helpers/enums";
import { load } from "helpers/filtersStorage";
import { isHidden } from "helpers/hiddenData";
import { report } from "helpers/models";
import { viewContainer } from "helpers/redux";
import { getStoredGroupByCurrentLogist, REPORT_TYPES, REPORT_TYPES_WITH_ODOMETER_MILEAGES } from "helpers/reports";
import { flagVal, ymEvent } from "helpers/system";
import pick from "lodash/pick";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React from "react";
import { alert } from "services/alert";
import {
    download,
    generateOverallReport,
    generatePlanPointIntersectionsReport,
    getGeneratedReport,
    getItem,
    getQueryByReportName,
    incomesAndExpensesReportDemo,
} from "sources/books/reports";
import { initialState } from "../formConfig";
import Filters, { componentFiltersMapper } from "./filters";
import * as ReportMarkups from "./markup";
import ReportTaskGate from "../reportTaskGate";
import ReportDataControls from "./reportDataControls";

const hourlyAnalyzeFlagValue = flagVal(organizationFlags, organizationFlagsEnum.AnalyzeOverallReportByHourlyMileage);
const useOdometerMileageFlagVal = flagVal(organizationFlags, organizationFlagsEnum.UseOdometerMileage);

export const HOURLY_REPORT_STARTED_AFTER = "2022-07-04 00:00:00+00:00";

class ShowReport extends BookShow {
    state = {
        startDate: moment().startOf("day").subtract(14, "days"),
        endDate: moment().endOf("day"),
        isLoading: true,
        isLoaded: false,
        extraParams: {},
        uniqKey: Date.now(),
        demoData: false,

        carTypeId: null,
        trailerTypeId: null,
        transportColumnId: null,
        responsibleId: null,
        multipleCarTypeId: null,
        withInternationalBids: false,
        withoutInternationalBids: false,
        sumWithoutVat: false,
        overallReportWithNds: true,
        byEmptyMileage: false,
        groupByCreator: false,
        filterByDryingPeriods: false,
        excludeBidsWithSeasonalRestrictions: false,
        groupByBidLogist: false,
        groupByCurrentLogist: getStoredGroupByCurrentLogist(),
        showInitialPlanValues: false,
        bidId: 1,
        onlyWithoutApp: false,
        filterOnlyByEndDate: false,
        firstPoint: null,
        lastPoint: null,
        organizationId: null,
        legalPersonId: null,
        year: moment().year(),
        month: moment().format("MM"),
        reportPeriod: `${moment().year()}-${moment().format("MM")}-01T00:00:00.00Z`,
        saveState: false,
        showFullMileages: true,
        marginalityValues: "all",
        type: "All",
        parametersObject: { radius: 1000 },
    };

    componentDidMount() {
        const { initialState, organization, bgTasks, taskKey, id, regionId, regionType } = this.props;
        const defStateByRegion = regionId
            ? {
                  startDate: moment().startOf("day").subtract(1, "month"),
                  endDate: moment().endOf("day"),
                  firstPoint: { id: Number(regionId), type: regionType },
              }
            : {};

        this.hourlyAnalyzeEnabled =
            (REPORT_TYPES.OverallReport === id &&
                (hourlyAnalyzeFlagValue & organization.intFlags) === hourlyAnalyzeFlagValue) ||
            REPORT_TYPES.PlanPointIntersectionsReport === id;
        const bgTask = taskKey ? bgTasks[taskKey] : undefined;
        let { startDate, endDate } = bgTask || this.state;
        if (id === REPORT_TYPES.DailyVehicleMileageReport) {
            startDate = moment().startOf("M");
        }
        this.setState(
            {
                data: initialState,
                initial: initialState,
                startDate,
                endDate,
                ...load("reports", {}),
                ...defStateByRegion,
                groupByCurrentLogist: getStoredGroupByCurrentLogist(),
            },
            () => setTimeout(() => this.getItem(undefined, this.props.taskKey, true), 0),
        );
    }

    componentWillUnmount() {
        this.reportRequestId += 1;
    }

    componentDidUpdate(prevProps) {
        const { params } = this.props.match;
        if (params.id !== prevProps.match.params.id) {
            this.printTableState = undefined;
            this.printTableStateReportId = undefined;
            const extraState = [REPORT_TYPES.OverallReport, REPORT_TYPES.MonthlyOverallReport].includes(params.id)
                ? { groupByCurrentLogist: getStoredGroupByCurrentLogist() }
                : {};
            this.setState({ isLoading: true, ...extraState }, () => this.getItem(this.props, undefined, true));
        }
    }

    get dropdActions() {
        if (this.props.id === REPORT_TYPES.RefuelingsVisitStatsReport) {
            return [];
        }
        const { data } = this.state;
        const { isTruckingOrganizationDispatcher } = this.props.roles;
        const actions = [{ text: "Печать", action: this.actionPrint }];
        if (
            data &&
            data.data.length &&
            !isHidden(isTruckingOrganizationDispatcher) &&
            this.props.id !== REPORT_TYPES.PlanPointIntersectionsReport
        ) {
            if ([REPORT_TYPES.MonthlyOverallReport, REPORT_TYPES.OverallReport].includes(this.props.id)) {
                // переделать этот отчет на withGrouping
                actions.push({ text: "Скачать с группировкой", action: () => this.downloadExcel() });
                actions.push({
                    text: "Скачать без группировки",
                    action: () => this.downloadExcel({ withoutGrouping: true }),
                });
            } else {
                actions.push({ text: "Скачать", action: () => this.downloadExcel() });
            }

            if ([REPORT_TYPES.MarginalityReport, REPORT_TYPES.ResponsibleLogistsReport].includes(this.props.id)) {
                actions.push({
                    text: "Скачать без группировки",
                    action: () => this.downloadExcel({ withGrouping: false }),
                });
            }
        }
        return actions;
    }

    reportComponent = () => {
        if (this.props.id === REPORT_TYPES.MonthlyOverallReport) {
            return ReportMarkups.OverallReport;
        }

        return (
            ReportMarkups[this.props.id] ??
            ((this.state.data.columns || []).some((c) => c.propertyName === "CarLogistId")
                ? ReportMarkups.GroupedByLogistReport
                : ReportMarkups.CommonReport)
        );
    };

    changeDemoData = (value) => {
        this.setState({ demoData: value }, () => this.getItem());
    };

    renderHead = () => {
        const { id, organization } = this.props;
        const { demoData } = this.state;
        const useOdometerMileageEnabled =
            REPORT_TYPES_WITH_ODOMETER_MILEAGES.includes(id) &&
            (organization.intFlags & useOdometerMileageFlagVal) === useOdometerMileageFlagVal;
        const additionalText = "(пробег рассчитан на основе одометра)";
        return (
            <div className="book-show__head">
                <div className="book-show__close close close--sm" onClick={this.props.back} />
                <div className="book-show__title">
                    {this.state.data?.name || ""}&nbsp;
                    {useOdometerMileageEnabled && <small>{additionalText}</small>}
                    {id === REPORT_TYPES.IncomesAndExpensesReport && (
                        <ReportDataControls demoData={demoData} onDemoChange={this.changeDemoData} />
                    )}
                    {id === REPORT_TYPES.PlanPointIntersectionsReport && (
                        <Tooltip
                            overlayClassName="tooltip--sm"
                            overlay={
                                <>
                                    Позволяет заранее увидеть планируемое время прибытия и пересечения границ геозон.
                                    Помогает избежать простоев и координировать логистику. <br />
                                    Нажмите для перехода к инструкции.
                                </>
                            }
                        >
                            <a
                                href="https://cargorun.omnidesk.ru/knowledge_base/item/372366?sid=67614"
                                target="_blank"
                                rel="noreferrer"
                                className="text-decoration-none"
                            >
                                <i className="icon-uEA69-question-circle pl-2 cursor-pointer inline-icon" />
                            </a>
                        </Tooltip>
                    )}
                </div>
                {!!this.dropdActions.length && (
                    <div className="book-show__dropdown c-dropd">
                        <div className="c-dropd__btn" onClick={this.openDropd} />
                        {this.renderDropd()}
                    </div>
                )}
            </div>
        );
    };

    actionPrint = () => {
        const printTableState = this.printTableStateReportId === this.props.id ? this.printTableState : undefined;

        return this.props.modal({
            name: "print",
            title: "Печать отчета",
            windowMod: "wide",
            CustomBody: this.reportComponent(),
            bodyData: {
                ...this.state,
                ...this.props,
                getItem: this.getItem,
                printTableState,
                isPrintForm: true,
            },
        });
    };

    setPrintTableState = (printTableState) => {
        this.printTableState = printTableState;
        this.printTableStateReportId = this.props.id;
    };

    filterData = ({ name, value, predicate }) => {
        const localFilterData = predicate && this.state.data.data.filter(predicate);
        this.setState({ [name]: value, localFilterData });
    };

    generateReportHandler = (key, bgTaskData) => {
        if (!key) {
            return this.setState({ isLoading: false });
        }

        const { startDate, endDate } = this.state;
        this.props.addBgTask({
            key,
            data: {
                key,
                startDate,
                endDate,
                title: "Формирование отчета",
                status: workloadStatusesEnum.Running,
                reportType: this.props.id,
                ...bgTaskData,
            },
        });
        alert({
            type: "info",
            title: "Формирование отчета",
            message: (
                <small>
                    <p>Отчет будет сформирован в фоновом режиме.</p>
                    Можете покинуть страницу, по завершении вы получите уведомление.
                </small>
            ),
        });
    };

    pointIntersectionsReportReqData = () => {
        const { parametersObject } = this.state;

        const reqParams = { radius: parametersObject.radius };

        if (parametersObject.firstLocation) {
            reqParams.firstLocation = { coordinates: parametersObject.firstLocation.location?.coordinates };
        }
        if (parametersObject.secondLocation) {
            reqParams.secondLocation = { coordinates: parametersObject.secondLocation.location?.coordinates };
        }
        return { req: generatePlanPointIntersectionsReport, reqParams };
    };

    getItem = (extraParams, taskKey = undefined, isInitialReq = false) => {
        const { id } = this.props;
        const requestId = (this.reportRequestId || 0) + 1;
        this.reportRequestId = requestId;
        this.setState({ isLoading: true, reportError: "" });
        if (extraParams) {
            this.setState({
                extraParams,
                ...(extraParams.groupByCurrentLogist !== undefined
                    ? { groupByCurrentLogist: extraParams.groupByCurrentLogist }
                    : {}),
            });
        }
        const defParams = { isLoading: false, isLoaded: true, localFilterData: null, bidId: 1 };
        const { demoData, startDate, endDate } = this.state;
        if (demoData) {
            return incomesAndExpensesReportDemo().then((data) => {
                if (requestId !== this.reportRequestId) {
                    return;
                }
                this.setState({
                    data: { ...(data || report), startDate, endDate },
                    dataLoadedAt: Date.now(),
                    ...defParams,
                });
            });
        }
        const params = { ...this.requestParams(!!extraParams), ...extraParams };

        if (taskKey) {
            return getGeneratedReport(taskKey).then((data) => {
                this.props.removeBgTask({ key: taskKey });
                this.setState({ data: { ...report, ...data, startDate, endDate }, ...defParams });
            });
        }

        if (REPORT_TYPES.PlanPointIntersectionsReport === id) {
            if (isInitialReq) {
                return this.setState({
                    data: { ...report, name: "Поиск предстоящего проезда через геозоны", startDate, endDate },
                    dataLoadedAt: Date.now(),
                    ...defParams,
                });
            }

            const { req, reqParams } = this.pointIntersectionsReportReqData();
            ymEvent("PlanPointIntersectionsReportDataUpdated");
            return req({ parametersObject: reqParams, generateFile: false }).then((key) =>
                this.generateReportHandler(key, { taskType: BG_TASK_TYPES.GenerateReport }),
            );
        }

        if (id === REPORT_TYPES.OverallReport && moment(endDate).diff(moment(startDate), "months") > 2) {
            const query = getQueryByReportName(params);
            const [parametersQuery, filterQuery] = query.split("&");
            return generateOverallReport({ parametersQuery, filterQuery, generateFile: false }).then((key) =>
                this.generateReportHandler(key, { taskType: BG_TASK_TYPES.GenerateReport }),
            );
        }

        if (id === REPORT_TYPES.IncomesAndExpensesReport) {
            ymEvent("UpdateExpenses");
        }

        return getItem(params)
            .then((data) => {
                if (this.reportRequestId !== requestId) {
                    return;
                }
                if (isInitialReq && data?.data?.length === 0 && id === REPORT_TYPES.IncomesAndExpensesReport) {
                    this.changeDemoData(true);
                }
                return this.setState({
                    data: { ...(data || report), startDate, endDate },
                    dataLoadedAt: Date.now(),
                    ...defParams,
                });
            })
            .catch(() => {
                if (this.reportRequestId === requestId) {
                    this.setState({ ...defParams, reportError: "Не удалось загрузить отчёт. Повторите запрос." });
                }
            });
    };

    componentFilters = () => {
        const filters = pick(this.state, componentFiltersMapper[this.props.id] || []);
        Object.keys(filters).forEach((key) => {
            if (filters[key] === undefined || filters[key] === null || filters[key]?.length === 0) {
                delete filters[key];
            }
        });
        return filters;
    };

    requestParams = (withoutExtraParams = false) => {
        const { id, organizationTimezone } = this.props;
        if (
            [
                REPORT_TYPES.CarsWithoutBidsReport,
                REPORT_TYPES.DriverAppInstallReport,
                REPORT_TYPES.FuelCardsPaymentReport,
                REPORT_TYPES.ClientStatisticsReport,
                REPORT_TYPES.MonthlyOverallReport,
            ].includes(id)
        ) {
            return { id, ...this.componentFilters(), ...(withoutExtraParams ? {} : this.state.extraParams) };
        }

        const { startDate, endDate } = this.state;
        return {
            id,
            startDate: utcTimeInOffset(startDate, organizationTimezone.utcOffset),
            endDate: utcTimeInOffset(endDate, organizationTimezone.utcOffset),
            ...(withoutExtraParams ? {} : this.state.extraParams),
            ...this.componentFilters(),
        };
    };

    downloadExcel = (additionalParams = {}) => {
        this.setState({ isLoading: true });
        const { startDate, endDate, data } = this.state;
        const { id } = this.props;
        const { withoutGrouping } = additionalParams;
        if (withoutGrouping) {
            delete additionalParams.withoutGrouping;
            additionalParams.id =
                id === REPORT_TYPES.OverallReport ? "PlainOverallReport" : "MonthlyPlainOverallReport";
        }
        const fileName = `${data.name}_${dateToView(startDate)}-${dateToView(endDate)}.xlsx`;
        const params = {
            ...this.requestParams(),
            ...additionalParams,
            fileName,
        };

        if (id === REPORT_TYPES.OverallReport && moment(endDate).diff(moment(startDate), "months") > 2) {
            const query = getQueryByReportName(this.requestParams());
            const [parametersQuery, filterQuery] = query.split("&");
            return generateOverallReport({ parametersQuery, filterQuery, generateFile: true }).then((key) =>
                this.generateReportHandler(key, { taskType: BG_TASK_TYPES.DownloadGeneratedReport, fileName }),
            );
        }

        if (REPORT_TYPES.PlanPointIntersectionsReport === id) {
            const { req, reqParams } = this.pointIntersectionsReportReqData();
            return req({ parametersObject: reqParams, generateFile: true }).then((key) =>
                this.generateReportHandler(key, { taskType: BG_TASK_TYPES.DownloadGeneratedReport, fileName }),
            );
        }

        return download(params)
            .then(() => this.setState({ isLoading: false }))
            .catch(() =>
                this.setState({ isLoading: false, reportError: "Не удалось выгрузить отчёт. Повторите запрос." }),
            );
    };

    forceUpdateReport = () => this.setState({ uniqKey: Date.now() });

    renderBodyWrap = () => {
        const { isLoading, isLoaded, startDate } = this.state;
        const { id, enums, switchMap, history, profile, organizationTimezone, organization } = this.props;

        if (!isLoaded) {
            return <Loader prefix="absolute-center" />;
        }
        return (
            <>
                {isLoading && (
                    <div className="loader-snippet--disable-loader">
                        <Loader />
                    </div>
                )}
                <div className="h-100 book-list">
                    {this.hourlyAnalyzeEnabled && startDate < moment(HOURLY_REPORT_STARTED_AFTER) && (
                        <div className="alert alert-warning font-size-14">
                            Даты отчета попадают в диапазон от {dateTimeToView(HOURLY_REPORT_STARTED_AFTER)} и ранее.
                            Отчет будет сформирован на основе анализа суточных пробегов
                        </div>
                    )}
                    <div className="planning-table__header">
                        <div className="d-flex justify-content-between w-100">
                            <Filters
                                getItem={this.getItem}
                                id={id}
                                filterData={this.filterData}
                                switchMap={switchMap}
                                forceUpdateReport={this.forceUpdateReport}
                                state={this.state}
                                setState={this.setState.bind(this)}
                                hourlyAnalyzeEnabled={this.hourlyAnalyzeEnabled}
                                history={history}
                                enums={enums}
                                profile={profile}
                                organizationTimezone={organizationTimezone}
                                organization={organization}
                            />
                        </div>
                    </div>
                    {this.state.reportError && (
                        <div className="alert alert-danger mx-3" role="alert">
                            {this.state.reportError}
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary ml-3"
                                onClick={() => this.getItem()}
                            >
                                Повторить
                            </button>
                        </div>
                    )}
                    <div className="list__body-wrap">
                        <div className="list__body pb-3 d-flex flex-column">{this.renderBody()}</div>
                    </div>
                </div>
            </>
        );
    };

    renderBody = () => {
        const ReportComponent = this.reportComponent();
        return (
            <ReportComponent
                key={this.state.uniqKey}
                {...this.state}
                {...this.props}
                getItem={this.getItem}
                setPrintTableState={this.setPrintTableState}
                requestParams={this.requestParams}
            />
        );
    };
}

const ConfiguredShow = (props) => (
    <ReportTaskGate {...props}>
        {(taskKey) => <ShowReport {...props} taskKey={taskKey} initialState={initialState} />}
    </ReportTaskGate>
);

export default viewContainer(ConfiguredShow, "reports");

export { ShowReport };
